import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { LifePlan } from '@core/models/life-plan.model';
import { LifePlanManagerService } from '@core/services/life-plan-manager.service';
import { Store } from '@ngrx/store';
import { AppState } from '@redux/app.reducers';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/**
 * This guards controls that the life plan required on the agent forms exists
 * and that it is in the DRAFT status.
 */
@Injectable({
  providedIn: 'root'
})
export class AgentFormGuard implements CanActivate {
  currentLifePlan?: LifePlan;

  constructor(private lifePlanManager: LifePlanManagerService, private router: Router, private store: Store<AppState>) {
    this.store.subscribe({
      next: state => {
        this.currentLifePlan = state.lifePlan.currentLifePlan;
      }
    });
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const urlPrefix = route.routeConfig?.path?.split('/').filter(element => element !== '')[0];
    const lifePlanId = route.paramMap.get('lifePlanId');
    if (!lifePlanId) {
      return false;
    }
    if (lifePlanId === this.currentLifePlan?.id && this.currentLifePlan.status === 'DRAFT') {
      // Do not perform API calls if the store has the current life plan
      return true;
    }
    return this.lifePlanManager.getAndManageLifePlan(lifePlanId).pipe(
      map(lifePlan => {
        if (!lifePlan || lifePlan.status !== 'DRAFT') {
          return this.router.parseUrl(`/${urlPrefix}/life-plans/${lifePlanId}`);
        }
        const formName = route.url[4].path;
        if (formName === 'personal') {
          return true;
        }
        if (formName === 'professional') {
          if (!this.personalDataCompleated(lifePlan)) {
            return this.router.parseUrl(`agent/life-plans/${lifePlanId}/form/personal`);
          }
          return true;
        }
        if (formName === 'financial') {
          if (!this.personalDataCompleated(lifePlan)) {
            return this.router.parseUrl(`agent/life-plans/${lifePlanId}/form/personal`);
          }
          if (!this.professionalDataCompleated(lifePlan)) {
            return this.router.parseUrl(`agent/life-plans/${lifePlanId}/form/professional`);
          }
          return true;
        }
        if (formName === 'insurances') {
          if (!this.personalDataCompleated(lifePlan)) {
            return this.router.parseUrl(`agent/life-plans/${lifePlanId}/form/personal`);
          }
          if (!this.professionalDataCompleated(lifePlan)) {
            return this.router.parseUrl(`agent/life-plans/${lifePlanId}/form/professional`);
          }
          if (!this.financialDataCompleated(lifePlan)) {
            return this.router.parseUrl(`agent/life-plans/${lifePlanId}/form/financial`);
          }
          return true;
        }
        return true;
      }),
      catchError(err => {
        console.error('Error in current life plan guard', err);
        let url = '/home';
        if (urlPrefix === 'agent') {
          url = '/agent/life-plans';
        }
        const tree: UrlTree = this.router.parseUrl(url);
        return of(tree);
      })
    );
  }

  private personalDataCompleated(lifePlan: LifePlan): boolean {
    if (lifePlan.user?.personal) {
      const personalData = lifePlan.user.personal;
      if (
        !personalData.name ||
        !personalData.birthDate ||
        !personalData.gender ||
        !personalData.email ||
        !personalData.phone ||
        !personalData.civilStatus
      ) {
        return false;
      }
      if (lifePlan.spouse?.personal) {
        const spousePersonalData = lifePlan.spouse.personal;
        if (!spousePersonalData.name || !spousePersonalData.birthDate || !spousePersonalData.gender) {
          return false;
        }
      }
    } else {
      return false;
    }
    return true;
  }

  private professionalDataCompleated(lifePlan: LifePlan): boolean {
    if (lifePlan.user?.professional) {
      const professionalData = lifePlan.user.professional;
      if (
        professionalData.contributionYears === undefined ||
        !professionalData.employment ||
        !professionalData.employmentRelationship
      ) {
        return false;
      }
      if (lifePlan.spouse?.professional) {
        const spouseProfessionalData = lifePlan.spouse.professional;
        if (
          spouseProfessionalData.contributionYears === undefined ||
          !spouseProfessionalData.employment ||
          !spouseProfessionalData.employmentRelationship
        ) {
          return false;
        }
      }
    } else {
      return false;
    }
    return true;
  }

  private financialDataCompleated(lifePlan: LifePlan): boolean {
    if (lifePlan.user?.financial) {
      const financialData = lifePlan.user.financial;
      if (!financialData.netMonthlyIncome?.amount || !financialData.investorProfile) {
        return false;
      }
      if (financialData.monthlyExpenditureType === 'general' && !financialData.generalMonthlyExpenditure?.amount) {
        return false;
      }
      if (financialData.monthlyExpenditureType === 'detailed') {
        if (financialData.detailedMonthlyExpenditure?.some(dme => !dme.type || !dme.amount.amount)) {
          return false;
        }
      }
      if (financialData.savingsAndInvestments?.some(si => !si.type || !si.amount.amount)) {
        return false;
      }
      if (
        financialData.futurePlans?.some(
          fp => !fp.type || !fp.assuredAmount.amount || !fp.goalAmount.amount || !fp.goalDate
        )
      ) {
        return false;
      }
      if (
        financialData.loans?.some(
          l => !l.type || !l.pendingAmount.amount || !l.monthlyFee.amount || l.remainingYears === undefined
        )
      ) {
        return false;
      }
      if (lifePlan.spouse?.financial) {
        const spouseFinancialData = lifePlan.spouse.financial;
        if (!spouseFinancialData.netMonthlyIncome?.amount) {
          return false;
        }
      }
    } else {
      return false;
    }
    return true;
  }
}
